import type { Message } from "../types"

interface MessageListProps {
  messages: Message[]
  primaryColor: string
  isLoading?: boolean
  onEndConversation?: () => void
  onShowProducts?: () => void
  onShowServices?: () => void
  onShowBookingForm?: () => void
  onShowPaymentOptions?: () => void
  onShowInvoice?: () => void
  onRequestFeedback?: () => void
  onShowCart?: () => void
}

export function MessageList({ 
  messages, 
  primaryColor, 
  isLoading = false, 
  onEndConversation,
  onShowProducts,
  onShowServices,
  onShowBookingForm,
  onShowPaymentOptions,
  onShowInvoice,
  onRequestFeedback,
  onShowCart
}: MessageListProps) {
  const adjustColor = (color: string, percent: number): string => {
    const num = parseInt(color.replace("#", ""), 16)
    const amt = Math.round(2.55 * percent)
    const R = (num >> 16) + amt
    const G = (num >> 8 & 0x00FF) + amt
    const B = (num & 0x0000FF) + amt
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
  }

  const buttonStyle = {
    padding: '8px 16px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease'
  }

  const ThinkingIndicator = () => (
    <div style={{
      marginBottom: '16px',
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-start',
      alignItems: 'flex-end'
    }}>
      <div style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        flexShrink: 0,
        background: `linear-gradient(135deg, ${primaryColor}, ${adjustColor(primaryColor, -10)})`,
        color: 'white',
        marginBottom: '2px'
      }}>
        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </div>
      
      <div style={{
        maxWidth: '85%',
        padding: '12px 16px',
        borderRadius: '16px',
        borderBottomLeftRadius: '4px',
        fontSize: '14px',
        lineHeight: '1.5',
        backgroundColor: '#f8f9fa',
        color: '#1f2937',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div style={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#6b7280',
            animation: 'typing 1.4s infinite ease-in-out'
          }}></div>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#6b7280',
            animation: 'typing 1.4s infinite ease-in-out',
            animationDelay: '0.2s'
          }}></div>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#6b7280',
            animation: 'typing 1.4s infinite ease-in-out',
            animationDelay: '0.4s'
          }}></div>
        </div>
        <span style={{ color: '#6b7280', fontSize: '13px' }}>AI is thinking...</span>
      </div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {messages.map((message) => (
        <div
          key={message.id}
          style={{
            display: 'flex',
            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: '16px',
            alignItems: 'flex-end',
            gap: '8px'
          }}
        >
          {message.role === 'assistant' && (
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${primaryColor}, ${adjustColor(primaryColor, -10)})`,
              color: 'white',
              marginBottom: '2px'
            }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
          )}
          
          <div style={{
            maxWidth: '85%',
            padding: '12px 16px',
            borderRadius: '16px',
            borderBottomLeftRadius: message.role === 'assistant' ? '4px' : '16px',
            borderBottomRightRadius: message.role === 'user' ? '4px' : '16px',
            fontSize: '14px',
            lineHeight: '1.5',
            wordWrap: 'break-word',
            backgroundColor: message.role === 'assistant' ? '#f8f9fa' : primaryColor,
            color: message.role === 'assistant' ? '#1f2937' : 'white',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            marginLeft: message.role === 'user' ? 'auto' : '0'
          }}>
            <div>
              {message.content || (message.isStreaming ? (
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  padding: '8px 0',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6b7280',
                    animation: 'typing 1.4s infinite ease-in-out'
                  }}></div>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6b7280',
                    animation: 'typing 1.4s infinite ease-in-out',
                    animationDelay: '0.2s'
                  }}></div>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6b7280',
                    animation: 'typing 1.4s infinite ease-in-out',
                    animationDelay: '0.4s'
                  }}></div>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  padding: '8px 0',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6b7280',
                    animation: 'typing 1.4s infinite ease-in-out'
                  }}></div>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6b7280',
                    animation: 'typing 1.4s infinite ease-in-out',
                    animationDelay: '0.2s'
                  }}></div>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6b7280',
                    animation: 'typing 1.4s infinite ease-in-out',
                    animationDelay: '0.4s'
                  }}></div>
                </div>
              ))}
              
              {/* Special Command Buttons */}
              {message.role === 'assistant' && message.metadata && (
                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {message.metadata.showProducts && onShowProducts && (
                    <button onClick={onShowProducts} style={buttonStyle}>
                      🛍️ View Products
                    </button>
                  )}
                  {message.metadata.showServices && onShowServices && (
                    <button onClick={onShowServices} style={buttonStyle}>
                      🛎️ View Services
                    </button>
                  )}
                  {message.metadata.showBookingForm && onShowBookingForm && (
                    <button onClick={onShowBookingForm} style={buttonStyle}>
                      📅 Book Now
                    </button>
                  )}
                  {message.metadata.showPaymentOptions && onShowPaymentOptions && (
                    <button onClick={onShowPaymentOptions} style={buttonStyle}>
                      💳 Payment Options
                    </button>
                  )}
                  {message.metadata.showInvoice && onShowInvoice && (
                    <button onClick={onShowInvoice} style={buttonStyle}>
                      🧾 View Invoice
                    </button>
                  )}
                  {message.metadata.requestFeedback && onRequestFeedback && (
                    <button onClick={onRequestFeedback} style={buttonStyle}>
                      ⭐ Rate Experience
                    </button>
                  )}
                  {message.metadata.showCart && onShowCart && (
                    <button onClick={onShowCart} style={buttonStyle}>
                      🛒 View Cart
                    </button>
                  )}
                  {message.metadata.showEndButton && onEndConversation && (
                    <button onClick={onEndConversation} style={buttonStyle}>
                      ✅ End Conversation
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {message.timestamp && (
              <div style={{
                fontSize: '10px',
                color: message.role === 'assistant' ? '#9ca3af' : 'rgba(255,255,255,0.7)',
                marginTop: '6px',
                textAlign: message.role === 'user' ? 'right' : 'left'
              }}>
                {new Date(message.timestamp).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            )}
          </div>

          {message.role === 'user' && (
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              flexShrink: 0,
              background: 'linear-gradient(135deg, #6b7280, #4b5563)',
              color: 'white',
              marginBottom: '2px'
            }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
        </div>
      ))}
      
      {/* Show thinking indicator when AI is loading but not streaming */}
      {isLoading && !messages.some(msg => msg.isStreaming) && <ThinkingIndicator />}
    </div>
  )
}